/**
 * @fileoverview Client-side DataTables renderer
 *
 * Renders tables using DataTables with client-side data.
 * Best for small to medium datasets (<1000 rows).
 *
 * @module renderers/DataTablesRenderer
 */

import { BaseRenderer } from './BaseRenderer.js';
import { DEFAULTS } from '../constants.js';

/**
 * DataTablesRenderer - Client-side DataTables rendering
 * @class
 * @extends BaseRenderer
 */
export class DataTablesRenderer extends BaseRenderer {
  /**
   * Create a DataTablesRenderer instance
   * @param {import('../types.js').RenderContext} context - Render context
   */
  constructor(context) {
    super(context);
    this.dataTable = null;
  }

  /**
   * Render table with DataTables
   * @override
   * @returns {Promise<void>}
   */
  async render() {
    if (!this.isDataTablesAvailable()) {
      throw new Error('DataTablesRenderer: DataTables library not available');
    }

    if (!Array.isArray(this.data)) {
      throw new Error('DataTablesRenderer: Data must be an array');
    }

    this.startRenderTiming();

    // Ensure table element exists
    this.ensureTableElement();

    // Apply modern styling
    this.applyModernStyling();

    // Build DataTables configuration
    const dtConfig = this._buildDataTablesConfig();

    try {
      // Initialize DataTable
      console.log(`DataTablesRenderer: Rendering ${this.data.length} rows`);
      this.dataTable = jQuery(this.tableElement).DataTable(dtConfig);

      this.endRenderTiming();
      this.isRendered = true;

      // Fix alignment when container becomes visible
      this._setupVisibilityHandler();

      // Call init callback
      if (this.options.onInit) {
        this.options.onInit(this);
      }

    } catch (error) {
      console.error('DataTablesRenderer: Initialization failed:', error);
      this.showError('Failed to initialize table: ' + error.message);
      throw error;
    }
  }

  /**
   * Build DataTables configuration
   * @private
   * @returns {Object} DataTables config
   */
  _buildDataTablesConfig() {
    // Process columns
    const columns = this._processColumns();

    // Determine sort order - disable if no columns exist
    let order = this.options.order || DEFAULTS.DEFAULT_ORDER;
    if (columns.length === 0) {
      order = [];
    } else if (Array.isArray(order) && order.length > 0) {
      // Validate that order references existing columns
      order = order.filter(o => Array.isArray(o) && o[0] < columns.length);
    }

    const config = {
      data: this.data,
      columns: columns,
      pageLength: this.options.pageLength || DEFAULTS.PAGE_LENGTH,
      lengthMenu: this.options.lengthMenu || DEFAULTS.LENGTH_MENU,
      searching: this.options.searching !== false,
      ordering: this.options.ordering !== false,
      paging: this.options.paging !== false,
      info: this.options.info !== false,
      dom: this.options.dom || DEFAULTS.DOM_LAYOUT,
      order: order,
      autoWidth: this.options.autoWidth !== undefined ? this.options.autoWidth : DEFAULTS.AUTO_WIDTH,
      destroy: true
    };

    // Add optional configurations
    if (this.options.language) {
      config.language = this.options.language;
    }
    if (this.options.scrollY) {
      config.scrollY = this.options.scrollY;
      config.scrollCollapse = this.options.scrollCollapse !== false;
    }
    if (this.options.select) {
      config.select = this.options.select;
    }
    if (this.options.buttons) {
      config.buttons = this.options.buttons;
    }

    // Column definitions for width and styling
    if (this.options.columns) {
      config.columnDefs = this._buildColumnDefs();
    }

    // Callbacks
    config.drawCallback = this.options.drawCallback;

    // Wrap rowCallback for row click handler
    if (this.options.onRowClick) {
      const originalRowCallback = this.options.rowCallback;
      config.rowCallback = (row, data, index) => {
        if (originalRowCallback) {
          originalRowCallback(row, data, index);
        }
        const clickHandler = () => {
          this.options.onRowClick(data, row, index);
        };
        jQuery(row).on('click', clickHandler);
        // Store handler for cleanup
        jQuery(row).data('cxgn-table-click-handler', clickHandler);
      };
    } else {
      config.rowCallback = this.options.rowCallback;
    }

    // Wrap initComplete for export buttons
    const userInitComplete = this.options.initComplete || this.options.fnInitComplete;
    config.initComplete = function(settings, json) {
      if (userInitComplete) {
        // Call with DataTables settings as 'this' context (provides this.api())
        userInitComplete.call(settings, settings, json);
      }
      // Export buttons would be added here by ExportManager
      if (this.options && this.options.onInitComplete) {
        this.options.onInitComplete(settings, json);
      }
    }.bind({ options: this.options });

    return config;
  }

  /**
   * Process columns configuration
   * @private
   * @returns {Array} Processed columns
   */
  _processColumns() {
    if (!this.options.columns || this.options.columns.length === 0) {
      // Try to extract column titles from existing <thead> <th> elements
      const existingHeaders = this._extractExistingHeaders();

      // Auto-generate columns from first data row
      if (this.data && this.data.length > 0) {
        const firstRow = this.data[0];
        if (Array.isArray(firstRow)) {
          return firstRow.map((_, i) => ({
            title: existingHeaders[i] || `Column ${i + 1}`,
            data: i
          }));
        } else if (typeof firstRow === 'object') {
          const keys = Object.keys(firstRow);
          return keys.map((key, i) => ({
            title: existingHeaders[i] || key.charAt(0).toUpperCase() + key.slice(1),
            data: key
          }));
        }
      }
      return [];
    }

    return this.options.columns.map(col => {
      const processed = { ...col };

      // Handle defaultContent
      if (col.defaultContent !== undefined) {
        processed.defaultContent = col.defaultContent;
      }

      return processed;
    });
  }

  /**
   * Extract column titles from existing <thead> <th> elements
   * @private
   * @returns {Array<string>} Array of header titles (may be empty)
   */
  _extractExistingHeaders() {
    if (!this.tableElement) {
      return [];
    }
    const ths = this.tableElement.querySelectorAll('thead th');
    return Array.from(ths).map(th => th.textContent.trim());
  }

  /**
   * Build column definitions for styling
   * @private
   * @returns {Array}
   */
  _buildColumnDefs() {
    const columnDefs = [];

    this.options.columns.forEach((col, i) => {
      const colDef = { targets: i };

      if (col.width) colDef.width = col.width;
      if (col.className) colDef.className = col.className;
      if (col.visible !== undefined) colDef.visible = col.visible;
      if (col.searchable !== undefined) colDef.searchable = col.searchable;
      if (col.orderable !== undefined) colDef.orderable = col.orderable;

      if (Object.keys(colDef).length > 1) {
        columnDefs.push(colDef);
      }
    });

    return columnDefs;
  }

  /**
   * Get DataTable instance
   * @returns {Object|null} DataTable instance
   */
  getDataTable() {
    return this.dataTable;
  }

  /**
   * Setup handlers to fix column alignment when table becomes visible
   * @private
   */
  _setupVisibilityHandler() {
    if (!this.dataTable) return;

    const adjustColumns = () => {
      if (this.dataTable && this.dataTable.columns) {
        this.dataTable.columns.adjust().draw(false);
      }
    };

    // Bootstrap tab shown event
    const tabSelector = `a[href="#${this.container.id}"], a[href="#${this.container.parentElement?.id}"]`;
    jQuery(document).on('shown.bs.tab', tabSelector, () => {
      setTimeout(adjustColumns, 100);
    });

    // Bootstrap collapse shown event
    jQuery(this.container).on('shown.bs.collapse', () => {
      setTimeout(adjustColumns, 100);
    });

    // Generic visibility change (for other UI frameworks)
    if (this.container) {
      const observer = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
          if (mutation.type === 'attributes' && (mutation.attributeName === 'style' || mutation.attributeName === 'class')) {
            const isVisible = this.container.offsetParent !== null;
            if (isVisible) {
              setTimeout(adjustColumns, 100);
            }
          }
        });
      });

      observer.observe(this.container, {
        attributes: true,
        attributeFilter: ['style', 'class']
      });

      // Store observer for cleanup
      this._visibilityObserver = observer;
    }

    // Also adjust on window resize
    jQuery(window).on('resize.datatable', () => {
      adjustColumns();
    });
  }

  /**
   * Refresh table (re-render with current data)
   */
  refresh() {
    if (this.dataTable) {
      this.dataTable.clear();
      this.dataTable.rows.add(this.data);
      this.dataTable.draw();
    }
  }

  /**
   * Clear table data
   */
  clear() {
    if (this.dataTable) {
      this.dataTable.clear().draw();
    }
  }

  /**
   * Update table data
   * @param {Array} newData - New data array
   */
  updateData(newData) {
    if (!Array.isArray(newData)) {
      throw new Error('DataTablesRenderer: Data must be an array');
    }

    this.data = newData;

    if (this.dataTable) {
      this.dataTable.clear();
      this.dataTable.rows.add(newData);
      this.dataTable.draw();
    }
  }

  /**
   * Search table
   * @param {string} term - Search term
   */
  search(term) {
    if (this.dataTable) {
      this.dataTable.search(term).draw();
    }
  }

  /**
   * Sort table
   * @param {number} columnIndex - Column index
   * @param {string} direction - Sort direction ('asc' or 'desc')
   */
  sort(columnIndex, direction) {
    if (this.dataTable) {
      this.dataTable.order([columnIndex, direction]).draw();
    }
  }

  /**
   * Get table data
   * @returns {Array}
   */
  getData() {
    if (this.dataTable) {
      return this.dataTable.rows().data().toArray();
    }
    return this.data || [];
  }

  /**
   * Get selected rows
   * @returns {Array}
   */
  getSelectedRows() {
    if (this.dataTable) {
      return this.dataTable.rows('.selected').data().toArray();
    }
    return [];
  }

  /**
   * Destroy renderer
   * @override
   */
  destroy() {
    // Remove window resize handler
    jQuery(window).off('resize.datatable');

    // Disconnect visibility observer
    if (this._visibilityObserver) {
      this._visibilityObserver.disconnect();
      this._visibilityObserver = null;
    }

    // Remove tab shown listeners
    jQuery(document).off('shown.bs.tab');

    if (this.dataTable) {
      try {
        this.dataTable.destroy();
      } catch (error) {
        console.warn('DataTablesRenderer: Error destroying DataTable:', error);
      }
      this.dataTable = null;
    }

    super.destroy();
  }

  /**
   * Proxy methods to DataTable API
   */

  column(...args) {
    return this.dataTable ? this.dataTable.column(...args) : null;
  }

  columns(...args) {
    return this.dataTable ? this.dataTable.columns(...args) : null;
  }

  row(...args) {
    return this.dataTable ? this.dataTable.row(...args) : null;
  }

  rows(...args) {
    return this.dataTable ? this.dataTable.rows(...args) : null;
  }

  cell(...args) {
    return this.dataTable ? this.dataTable.cell(...args) : null;
  }

  cells(...args) {
    return this.dataTable ? this.dataTable.cells(...args) : null;
  }

  draw(...args) {
    return this.dataTable ? this.dataTable.draw(...args) : null;
  }

  page(...args) {
    return this.dataTable ? this.dataTable.page(...args) : null;
  }

  order(...args) {
    return this.dataTable ? this.dataTable.order(...args) : null;
  }
}

export default DataTablesRenderer;
